import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { setupNavigationGuards } from './guards';
import SalesLayout from '@/views/main/MainLayout.vue'
import KitchenDisplayLayout from '@/views/kitchen/KitchenDisplayLayout.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'tenantLogin',
    meta: { darkStatusBar: true },
    component: () => import('@/views/auth/TenantLogin.vue'),
  },
  {
    path: '/register',
    name: 'tenantRegister',
    meta: { darkStatusBar: true },
    component: () => import('@/views/auth/TenantRegister.vue'),
  },
  {
    path: '/getting-started',
    name: 'gettingStarted',
    meta: { auth: 'tenant' },
    component: () => import('@/views/gettingStarted/GettingStarted.vue'),
  },
  {
    path: '/user-login',
    name: 'userLogin',
    meta: { auth: 'tenant', darkStatusBar: true },
    component: () => import('@/views/auth/UserLogin.vue'),
  },
  {
    path: '/debug-console',
    name: 'debugConsole',
    meta: { auth: 'tenant' },
    component: () => import('@/views/auth/DebugConsole.vue'),
  },
  {
    path: '/kitchen',
    name: 'kitchen',
    component: KitchenDisplayLayout,
    meta: { auth: 'tenant' },
    redirect: '/kitchen/open',
    children: [
      {
        path: 'open',
        name: 'kitchenOpenTickets',
        component: () => import('@/views/kitchen/KitchenDisplay.vue'),
      },
      {
        path: 'done',
        name: 'kitchenDoneTickets',
        component: () => import('@/views/kitchen/KitchenDisplay.vue'),
      },
    ],
  },
  {
    path: '/main/',
    name: 'main',
    component: SalesLayout,
    meta: { auth: 'user' },
    children: [
      {
        name: 'redirect',
        path: '',
        redirect: '/main/sales'
      },
      {
        name: 'sales',
        path: 'sales',
        component: () => import('@/views/main/sales/Sales.vue')
      },
      {
        name: 'salesOrder',
        path: 'sales/order',
        component: () => import('@/views/main/sales/hospitality/Order.vue')
      },
      {
        name: 'workingDay',
        path: 'working-day',
        component: () => import('@/views/main/workingDay/WorkingDay.vue')
      },
      {
        name: 'receipts',
        path: 'receipts',
        component: () => import('@/views/main/receipts/Receipts.vue')
      },
      {
        name: 'reports',
        path: 'reports',
        component: () => import('@/views/main/reports/Reports.vue')
      },
      {
        name: 'reportDetails',
        path: 'reports/details',
        component: () => import('@/views/main/reports/ReportDetails.vue')
      },
      {
        name: 'ordersHub',
        path: 'orders-hub',
        component: () => import('@/views/main/OrdersHubView.vue')
      },
      {
        name: 'options',
        path: 'options',
        component: () => import('@/views/main/options/Options.vue')
      },
      {
        name: 'printers',
        path: 'options/printers',
        component: () => import('@/views/main/options/Printers.vue')
      },
      {
        name: 'sdc',
        path: 'options/sdc',
        component: () => import('@/views/main/options/SdcManagement.vue')
      },
      {
        name: 'products',
        path: 'options/products',
        component: () => import('@/views/main/options/Products.vue')
      },
      {
        name: 'clients',
        path: 'clients',
        component: () => import('@/views/main/clients/Clients.vue')
      },
    ]
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

setupNavigationGuards(router)

// A route chunk built under a previous web deploy 404s once the deploy
// rotates out old assets — the tab has to reload to fetch the current
// build (Sentry KONTY-WEB-10 + KONTY-WEB-2; both left the user on a
// stuck screen). One attempt per route: while the OLD service worker is
// still in control, the reload itself can be served entirely from its
// stale cache and fail identically, so an unguarded reload would loop
// until the SW updates.
export const STALE_CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed/

const RELOAD_ATTEMPT_KEY = 'staleChunkReloadPath'

export function handleStaleChunkError(error: Error, to: { fullPath: string }): void {
  if (!STALE_CHUNK_ERROR.test(error.message)) return
  if (sessionStorage.getItem(RELOAD_ATTEMPT_KEY) === to.fullPath) return

  sessionStorage.setItem(RELOAD_ATTEMPT_KEY, to.fullPath)
  window.location.href = to.fullPath
}

router.onError(handleStaleChunkError)

// A navigation that succeeds means the loaded build is healthy — allow
// future stale-chunk incidents to auto-reload again.
router.afterEach(() => {
  sessionStorage.removeItem(RELOAD_ATTEMPT_KEY)
})

export { router }
